import React, { useState } from "react";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const META_APP_PERMISSIONS_URL =
  "https://developers.facebook.com/apps/333713976810748/app-review/permissions/";
const META_CONNECT_IG_URL = "https://www.facebook.com/business/help/898752960195806";
const META_IG_MESSAGING_URL =
  "https://developers.facebook.com/docs/messenger-platform/instagram/get-started/";
const META_PAGE_ROLES_URL = "https://www.facebook.com/business/help/1101781386943864";

/** Lê o aviso de portfólio da resposta nova ou, se a API ainda não enviar `notices`, dos hints. */
export function portfolioNoticeFromMetaList(res, diagnostics) {
  const notices = Array.isArray(res?.notices) ? res.notices : [];
  const fromApi = notices.find((n) => n?.code === "META_PORTFOLIO_UNAVAILABLE");
  if (fromApi) {
    return {
      code: "META_PORTFOLIO_UNAVAILABLE",
      listedFromAccount: fromApi.listedFromAccount ?? diagnostics?.sources?.meAccounts ?? null,
    };
  }
  const hints = diagnostics?.hints;
  const missing =
    Array.isArray(hints) &&
    hints.some(
      (h) =>
        String(h).includes("MISSING_SCOPE_business_management") ||
        String(h).includes("ME_BUSINESSES_FAILED")
    );
  if (!missing) return null;
  return {
    code: "META_PORTFOLIO_UNAVAILABLE",
    listedFromAccount: diagnostics?.sources?.meAccounts ?? null,
  };
}

function accountPageCountLabel(count) {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return null;
  return n === 1 ? "1 página" : `${n} páginas`;
}

/**
 * A Meta autenticou, mas não leu o portfólio empresarial.
 * @param {{ listedFromAccount?: number|null }} props
 */
export function MetaPortfolioPermissionAlert({ listedFromAccount }) {
  const countLabel = accountPageCountLabel(listedFromAccount);

  return (
    <Alert severity="warning" sx={{ mt: 1, alignItems: "flex-start" }}>
      <Typography variant="subtitle2" component="p" sx={{ mb: 0.75 }}>
        Algumas Páginas do portfólio não entraram na lista
      </Typography>
      <Typography variant="body2" component="p">
        A Meta aceitou o login, mas não liberou a leitura do portfólio empresarial
        (Gerenciador de Negócios). A lista mostra só as Páginas ligadas diretamente à sua
        conta
        {countLabel ? ` (${countLabel})` : ""}. Páginas que existem apenas dentro de um
        portfólio ficam de fora.
      </Typography>
      <Typography variant="body2" component="p" sx={{ mt: 1.25, fontWeight: 600 }}>
        Como incluir essas Páginas
      </Typography>
      <Box
        component="ol"
        sx={{
          m: 0,
          mt: 0.75,
          pl: 2.25,
          "& li": { mb: 0.75 },
          "& li:last-of-type": { mb: 0 },
        }}
      >
        <Typography component="li" variant="body2">
          Clique em «Atualizar lista de páginas» e conclua o login. Na tela da Meta, aceite o
          acesso às Páginas e ao portfólio empresarial, com a conta que administra ambos.
        </Typography>
        <Typography component="li" variant="body2">
          Se a Página continuar de fora, quem administra o app na Meta precisa solicitar acesso
          avançado para o portfólio empresarial e para a listagem de Páginas, e concluir a
          verificação comercial se a Meta pedir.{" "}
          <Link href={META_APP_PERMISSIONS_URL} target="_blank" rel="noreferrer">
            Abrir as permissões do app
          </Link>
          .
        </Typography>
        <Typography component="li" variant="body2">
          Depois da aprovação, volte aqui e clique de novo em «Atualizar lista de páginas».
        </Typography>
      </Box>
    </Alert>
  );
}

const stepListSx = {
  m: 0,
  mt: 0.75,
  pl: 2.25,
  "& li": { mb: 0.75 },
  "& li:last-of-type": { mb: 0 },
};

/** Passos para deixar uma conta de Instagram selecionável neste canal. */
export function MetaInstagramResolveSteps() {
  return (
    <Box component="ol" sx={stepListSx}>
      <Typography component="li" variant="body2">
        Entre com a conta Meta que administra a Página e o Instagram que você quer usar.
      </Typography>
      <Typography component="li" variant="body2">
        Confirme que o Instagram é profissional (Empresa ou Criador) e está conectado à Página.{" "}
        <Link href={META_CONNECT_IG_URL} target="_blank" rel="noreferrer">
          Ver como vincular na Meta
        </Link>
        .
      </Typography>
      <Typography component="li" variant="body2">
        Clique em «Atualizar lista de páginas». No login da Meta, marque essa Página e permita o
        acesso às mensagens.{" "}
        <Link href={META_IG_MESSAGING_URL} target="_blank" rel="noreferrer">
          Ver o que a Meta exige
        </Link>
        .
      </Typography>
    </Box>
  );
}

/**
 * Páginas do Facebook chegaram, mas nenhuma serve para o canal de Instagram.
 */
export function MetaInstagramUnavailableAlert() {
  return (
    <Alert severity="error" sx={{ mt: 1, alignItems: "flex-start" }}>
      <Typography variant="subtitle2" component="p" sx={{ mb: 0.75 }}>
        Nenhuma conta de Instagram pode ser selecionada
      </Typography>
      <Typography variant="body2" component="p">
        Encontramos Páginas do Facebook nesta conta, mas nenhuma tem um Instagram profissional
        vinculado. Sem esse vínculo, a Página aparece desabilitada e não pode ser usada neste
        canal.
      </Typography>
      <Typography variant="body2" component="p" sx={{ mt: 1.25, fontWeight: 600 }}>
        Como resolver
      </Typography>
      <MetaInstagramResolveSteps />
    </Alert>
  );
}

/** Passos quando o canal é Messenger e a lista de Páginas veio vazia ou falhou. */
export function MetaFacebookPagesHelp() {
  return (
    <Alert severity="warning" sx={{ mt: 1, alignItems: "flex-start" }}>
      <Typography variant="subtitle2" component="p" sx={{ mb: 0.75 }}>
        Como resolver
      </Typography>
      <Box
        component="ol"
        sx={{
          m: 0,
          pl: 2.25,
          "& li": { mb: 0.75 },
          "& li:last-of-type": { mb: 0 },
        }}
      >
        <Typography component="li" variant="body2">
          Confirme que você entrou com a conta Meta que administra a Página.
        </Typography>
        <Typography component="li" variant="body2">
          Verifique se a Página está publicada e com o Messenger ativo.
        </Typography>
        <Typography component="li" variant="body2">
          No login da Meta, selecione essa Página e permita o acesso às mensagens.{" "}
          <Link href={META_PAGE_ROLES_URL} target="_blank" rel="noreferrer">
            Ver acesso e funções na Página
          </Link>
          .
        </Typography>
      </Box>
    </Alert>
  );
}

/**
 * Diagnóstico bruto da listagem, só para suporte. Fecha por padrão para não competir com o aviso.
 */
export function MetaPagesSupportDetails({ diagnostics, requestId }) {
  const [open, setOpen] = useState(false);
  if (!diagnostics) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        color="inherit"
        onClick={() => setOpen((v) => !v)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ px: 0.5, color: "text.secondary", textTransform: "none" }}
      >
        Detalhes técnicos para suporte
      </Button>
      {open ? (
        <Box
          sx={{
            mt: 0.5,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "action.hover",
            maxHeight: 220,
            overflow: "auto",
          }}
        >
          {requestId ? (
            <Typography variant="caption" color="text.disabled" display="block">
              ID da requisição: {requestId}
            </Typography>
          ) : null}
          {diagnostics.sources ? (
            <Typography variant="caption" color="text.secondary" display="block">
              Páginas da conta: {diagnostics.sources.meAccounts ?? 0}. Portfólio:{" "}
              {diagnostics.sources.businessPortfolio ?? 0}. Completadas à parte:{" "}
              {diagnostics.sources.granularHydrated ?? 0}. Total:{" "}
              {diagnostics.merged?.count ?? 0}.
            </Typography>
          ) : null}
          {Array.isArray(diagnostics.hints) && diagnostics.hints.length > 0 ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {diagnostics.hints.join(" · ")}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            component="pre"
            color="text.disabled"
            sx={{ mt: 0.5, fontSize: "0.65rem", whiteSpace: "pre-wrap", mb: 0 }}
          >
            {JSON.stringify(diagnostics, null, 2)}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
